import { Keyframe } from './types/domain.types';

/**
 * Utility functions for keyframe operations
 */

/**
 * Round time value to 3 decimal places for consistency
 */
export const roundTime = (time: number): number => {
  return Math.round(time * 1000) / 1000;
};

/**
 * Sort keyframes by time position (ascending)
 */
export const sortKeyframesByTime = (keyframes: Keyframe[]): Keyframe[] => {
  return [...keyframes].sort((a, b) => a.timePosition - b.timePosition);
};

/**
 * Find the keyframe with the latest time position
 */
export const findLatestKeyframe = (keyframes: Keyframe[]): Keyframe | null => {
  if (keyframes.length === 0) return null;
  return keyframes.reduce((latest, current) => 
    current.timePosition > latest.timePosition ? current : latest
  );
};

/**
 * Get the first keyframe (earliest time position)
 */
export const getFirstKeyframe = (keyframes: Keyframe[]): Keyframe | undefined => {
  const sorted = sortKeyframesByTime(keyframes);
  return sorted[0];
};

/**
 * Get inherited values from a keyframe or return defaults
 */
export const getInheritedOrDefaultValues = (previousKeyframe?: Keyframe) => {
  if (previousKeyframe) {
    return {
      position: previousKeyframe.position,
      rotation: previousKeyframe.rotation,
      scale: previousKeyframe.scale
    };
  }
  
  return {
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 }
  };
};

/**
 * Determine if a keyframe should be categorized as 'end'
 */
export const shouldBeEndKeyframe = (
  newTimePosition: number,
  existingKeyframes: Keyframe[]
): boolean => {
  if (existingKeyframes.length === 0) return true;
  
  const latestKeyframe = findLatestKeyframe(existingKeyframes);
  return latestKeyframe ? newTimePosition > latestKeyframe.timePosition : true;
};

/**
 * Find the current 'end' keyframe
 */
export const findEndKeyframe = (keyframes: Keyframe[]): Keyframe | undefined => {
  return keyframes.find(kf => kf.category === 'end');
};

/**
 * Detect if asset movement is a drag vs resize
 */
export const isAssetDrag = (
  newStartTime: number,
  newEndTime: number,
  oldStartTime: number,
  oldEndTime: number,
  threshold: number = 0.01
): boolean => {
  const timeOffset = Math.abs(newStartTime - oldStartTime);
  const durationChange = Math.abs(
    (newEndTime - newStartTime) - (oldEndTime - oldStartTime)
  );
  
  return timeOffset > threshold && durationChange < threshold;
};

